<script setup lang="ts">
import './assets/style.scss'
import { computed, ref } from 'vue'
import { VPreloader } from '@/shared/ui'
import { VMapAreaSelect } from '@/shared/ui/VMapAreaSelect'
import { VMapMyPosition } from '@/shared/ui/VMapMyPosition'
import { VMapSelect } from '@/shared/ui/VMapSelect'
import { VMapZoom } from '@/shared/ui/VMapZoom'
import { useMapProvider } from '@/shared/lib'
import { WInventoryObjectCreateModal, WMapMoveConfirmPanel } from '@/widgets'
import { WInventoryLineCreateModal } from '@/widgets/InventoryLineCreateModal'
import { InventObject } from '@/widgets/InventoryMap/model/classes/InventObject.ts'
import { useInventoryMapController } from '@/widgets/InventoryMap/model/useInventoryMapController.ts'
import type { MapComponentExpose } from '@/widgets/InventoryMap/model/adapters/types.ts'
import { WInventoryMapInfo } from '@/widgets/InventoryMapInfo'
import WInventoryMapActions from './WInventoryMapActions.vue'
import WInventoryMapLeaflet from './WInventoryMapLeaflet.vue'
import WInventoryMapPointer from './WInventoryMapPointer.vue'
import WInventoryMapYandex from './WInventoryMapYandex.vue'
import { PassportRuntimeV2Modal } from '@/features/Inventory/PassportRuntimeV2'

const map = ref<MapComponentExpose>()
const { isChecking, isYmapsAvailable } = useMapProvider()

const {
  action,
  addObjectType,
  areaSearchSelected,
  canRelocate,
  objectMoveBlockedHandler,
  center,
  clickAddPointHandler,
  clickEditHandler,
  clickHandler,
  clusterSelectHandler,
  collapseStackHandler,
  dragEndEditHandler,
  dragMoveEditHandler,
  dragStartEditHandler,
  editFeature,
  editNewPoint,
  editSelectObject,
  filter,
  full,
  getFeatureStyle,
  inventObjectTypes,
  typesDict,
  isAreaSelectMapDragEnabled,
  isEditNewPoint,
  isLoadCenter,
  isMapBusy,
  isPlaceMode,
  mapCenteredHandler,
  mapObjectFocusedHandler,
  mapType,
  useAlternativeMap,
  mouseDownHandler,
  mouseMoveHandler,
  mouseUpHandler,
  objectClickHandler,
  objectEndDragHandler,
  objectMoveDragHandler,
  objectStartDragHandler,
  objects,
  objectsList,
  placePointerType,
  pointerCount,
  pointerScreenCoordinates,
  selectCoordsType,
  selectedHandler,
  selectedObjects,
  skipInitialBoundsFit,
  updateCoordsHandler,
  updateHandler,
  zoom,
  zoomSmallMarker,
  zoomSwitchValue,
} = useInventoryMapController(map)

/* Яндекс показываем, только если он доступен и пользователь не форсит альтернативную карту */
const useYandex = computed(() => isYmapsAvailable.value && !useAlternativeMap.value)

/**
 * Лёгкие «шапки» выбранных объектов для InventoryMapInfo — берём из уже
 * загруженного `objectsList`, без отдельного запроса (сервер гарантирует,
 * что выбранные объекты придут индивидуально благодаря `exclude_ids`).
 */
const selectedHeaders = computed(() =>
  selectedObjects.value
    .map((id) => (objectsList.value as InventObject[]).find((o) => o.getId() === id))
    .filter((o): o is InventObject => !!o)
    .map((o) => ({
      id: o.getId(),
      name: o.getName(),
      icon: o.getType().icon,
      typeName: o.getType().name,
      typeId: o.getObjectTypeId(),
      coordinates: o.getMapCoords(),
    })),
)

const sharedMapProps = computed(() => ({
  mapType: mapType.value,
  pointerCount: pointerCount.value,
  editFeature: editFeature.value,
  objects: objects.value,
  types: typesDict.value,
  action: action.value,
  zoomSwitchValue: zoomSwitchValue,
  zoomSmallMarker: zoomSmallMarker,
  editSelectObject: editSelectObject.value,
  editNewPoint: editNewPoint.value,
  isEditNewPoint: isEditNewPoint.value,
  isPlaceMode: isPlaceMode.value,
  placePointerType: placePointerType.value,
  addObjectType: addObjectType.value,
  pointerScreenCoordinates: pointerScreenCoordinates.value,
  getFeatureStyle,
  canRelocateObject: canRelocate,
  objectMoveBlockedHandler,
  objectClickHandler,
  objectEndDragHandler,
  objectStartDragHandler,
  objectMoveDragHandler,
  clusterSelectHandler,
  collapseStackHandler,
  dragStartEditHandler,
  dragMoveEditHandler,
  dragEndEditHandler,
  clickEditHandler,
  clickAddPointHandler,
  updateCoordsHandler,
  mapCenteredHandler,
  selectedHandler,
  updateHandler,
  mouseMoveHandler,
  clickHandler,
  mouseUpHandler,
  mouseDownHandler,
}))
</script>

<template>
  <div
    class="w-invent-map__grid"
    :class="{
      'w-invent-map__wrapper_full': full,
      'w-invent-map__wrapper_edit': addObjectType,
      'w-invent-map__wrapper_place': isPlaceMode,
    }"
  >
    <div class="w-invent-map__control mt-2">
      <WInventoryMapActions
        v-model="action"
        v-model:add_object_type="addObjectType"
        v-model:full="full"
        v-model:filter="filter"
        v-model:map_type="mapType"
        v-model:alternative_map="useAlternativeMap"
        v-model:select_coords_type="selectCoordsType"
        :ymaps-available="isYmapsAvailable"
        :zoom
        :items="inventObjectTypes"
        :object-items="objectsList as InventObject[]"
      />
    </div>

    <VPreloader
      v-if="isChecking || (isLoadCenter && !skipInitialBoundsFit)"
      :wrapper-height="300"
    />

    <template v-else>
      <component
        :is="useYandex ? WInventoryMapYandex : WInventoryMapLeaflet"
        ref="map"
        v-model:center="center"
        v-model:zoom="zoom"
        v-model:selected-objects="selectedObjects"
        v-bind="sharedMapProps"
        :select-coords-type="selectCoordsType"
        :is-area-select-map-drag-enabled="isAreaSelectMapDragEnabled"
      />

      <div v-if="isMapBusy" class="w-invent-map__loading-overlay">
        <VPreloader :wrapper-height="120" />
      </div>

      <div class="w-invent-map__overlay w-invent-map__overlay_left">
        <WMapMoveConfirmPanel />
      </div>

      <div class="w-invent-map__overlay w-invent-map__overlay_right">
        <VMapZoom v-model="zoom" />

        <VMapMyPosition v-model:zoom="zoom" v-model:center="center" />

        <WInventoryMapInfo
          v-model="selectedObjects"
          :headers="selectedHeaders"
          @update-coords="updateCoordsHandler"
          @map-object-focused="mapObjectFocusedHandler"
        />

        <WInventoryMapPointer
          :type="isPlaceMode ? placePointerType : addObjectType"
          :position="pointerScreenCoordinates"
        />
      </div>

      <div class="w-invent-map__area-overlay">
        <VMapSelect @selected="selectedHandler" />

        <VMapAreaSelect
          v-if="selectCoordsType"
          :type="selectCoordsType"
          @selected="areaSearchSelected"
        />
      </div>
    </template>
  </div>

  <WInventoryObjectCreateModal
    v-if="addObjectType"
    :functional-type="addObjectType.functional_type"
    :dependencies="addObjectType.dependencies_list"
  />

  <WInventoryLineCreateModal v-if="addObjectType" :dependencies="addObjectType.dependencies_list" />

  <PassportRuntimeV2Modal />
</template>
